<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import AgentsAPI from 'dashboard/api/agents/agents';
import FormScoringEditor from './FormScoringEditor.vue';
import { maxPoints as computeMaxPoints } from '../helpers/maxPoints';

const props = defineProps({
  fields: { type: Array, default: () => [] },
});
const { t } = useI18n();
const getters = useStoreGetters();
const store = useStore();

const funnelId = defineModel('funnelId', {
  type: [String, Number],
  default: '',
});
// Caixa onde as CONVERSAS dos leads são criadas. WhatsApp = o time chama o
// cliente direto na conversa; vazio = caixa padrão "Formulários" (só registro).
const inboxId = defineModel('inboxId', {
  type: [String, Number],
  default: null,
});
const stageName = defineModel('stageName', { type: String, default: '' });
// F3.1 — config da nutrição por IA ({ enabled, whatsapp_inbox_id, message_content, template_id }).
const aiNurture = defineModel('aiNurture', {
  type: Object,
  default: () => ({}),
});
const scoring = defineModel('scoring', { type: Object, default: () => ({}) });

const funnels = computed(() => getters['funnels/getFunnels'].value);

const stages = computed(() => {
  if (!funnelId.value) return [];
  return getters['funnels/getFunnelStages'].value(funnelId.value);
});

// Espelha Forms::ScoringService#max_points: maior peso em escolha única,
// soma dos positivos em checkbox. Serve só para o cliente conferir a escala.
const maxPoints = computed(() => computeMaxPoints(props.fields));

const onSelectFunnel = event => {
  funnelId.value = event.target.value;
  const validStage = stages.value.some(stage => stage.name === stageName.value);
  if (!validStage) {
    stageName.value = '';
  }
};

const whatsappInboxes = computed(
  () => getters['inboxes/getWhatsAppInboxes'].value
);

const allInboxes = computed(() => getters['inboxes/getInboxes'].value || []);

// Reatribui o objeto inteiro pra emitir o update:aiNurture (mutação de
// propriedade solta não dispara o v-model).
const updateNurture = (key, value) => {
  aiNurture.value = { ...aiNurture.value, [key]: value };
};

// Templates HSM só existem em inbox Meta oficial (whatsapp_cloud). Fora dele
// (WAHA/Evolution) usamos texto livre. Mostramos o seletor só quando há templates.
const inboxTemplates = computed(() => {
  const id = aiNurture.value?.whatsapp_inbox_id;
  if (!id) return [];
  return getters['inboxes/getWhatsAppTemplates'].value(id) || [];
});

// API oficial do Meta (Cloud): mensagem iniciada pela empresa (fora da janela de
// 24h) exige TEMPLATE aprovado — texto livre não é entregue. Avisamos quando o
// inbox escolhido é oficial e ainda não há template selecionado.
const OFFICIAL_PROVIDERS = ['default', 'whatsapp_cloud'];

const selectedNurtureInbox = computed(() =>
  whatsappInboxes.value.find(
    i => String(i.id) === String(aiNurture.value?.whatsapp_inbox_id)
  )
);

const isOfficialInbox = computed(() =>
  OFFICIAL_PROVIDERS.includes(selectedNurtureInbox.value?.provider)
);

const officialNeedsTemplate = computed(
  () => isOfficialInbox.value && !aiNurture.value?.template_id
);

// ---- Variáveis do template (mesmo sistema do canvas de campanhas) ----------
const selectedTemplate = computed(() => {
  const id = aiNurture.value?.template_id;
  if (!id) return null;
  return (
    inboxTemplates.value.find(
      tpl => String(tpl.id || tpl.name) === String(id)
    ) || null
  );
});

const templateBodyText = computed(
  () =>
    (selectedTemplate.value?.components || []).find(c => c.type === 'BODY')
      ?.text || ''
);

// Chaves {{1}}, {{2}}… extraídas do corpo (fonte da verdade é o texto).
const templateVariableKeys = computed(() => {
  const keys = [];
  const regex = /\{\{\s*(\d+)\s*\}\}/g;
  let match = regex.exec(templateBodyText.value);
  while (match) {
    if (!keys.includes(match[1])) keys.push(match[1]);
    match = regex.exec(templateBodyText.value);
  }
  return keys;
});

const templateVariables = computed(
  () => aiNurture.value?.template_variables || {}
);

// Rótulo visual da variável ("{{1}}") — montado no script porque chaves
// duplas literais quebram o parser de interpolação do template.
const varLabel = key => `{{${key}}}`;

const updateTemplateVariable = (key, value) => {
  updateNurture('template_variables', {
    ...templateVariables.value,
    [key]: value,
  });
};

// Amostras pro preview (os tokens são resolvidos por lead no envio real).
const previewSample = value => {
  const raw = (value || '').trim();
  if (!raw) return '‹vazio›';
  return raw
    .replace(/\{\{\s*nome\s*\}\}/gi, 'João Silva')
    .replace(/\{\{\s*empresa\s*\}\}/gi, 'Empresa Exemplo');
};

// Corpo quebrado em partes texto/variável — variável preenchida ganha
// destaque azul no preview (referência NexTags).
const previewParts = computed(() => {
  const body = templateBodyText.value;
  if (!body) return [];
  const parts = [];
  const regex = /\{\{\s*(\d+)\s*\}\}/g;
  let last = 0;
  let match = regex.exec(body);
  while (match) {
    if (match.index > last) {
      parts.push({ isVar: false, text: body.slice(last, match.index) });
    }
    parts.push({
      isVar: true,
      text: previewSample(templateVariables.value[match[1]]),
    });
    last = match.index + match[0].length;
    match = regex.exec(body);
  }
  if (last < body.length) parts.push({ isVar: false, text: body.slice(last) });
  return parts;
});

const onSelectTemplate = value => {
  // Troca de template: começa com {{1}} = nome (convenção) e o resto vazio.
  const patch = { ...aiNurture.value, template_id: value };
  if (value) {
    patch.template_variables = { 1: '{{nome}}' };
  } else {
    patch.template_variables = {};
  }
  aiNurture.value = patch;
};

// Agente que responde o lead depois da abertura. Vazio = agente do canal.
const aiAgents = ref([]);

onMounted(async () => {
  try {
    const { data } = await AgentsAPI.get();
    aiAgents.value = (Array.isArray(data) ? data : data?.payload || []).filter(
      agent => agent.active
    );
  } catch {
    aiAgents.value = [];
  }
});

const isSyncingTemplates = ref(false);

// Puxa os templates aprovados do número oficial direto da Meta e recarrega os
// inboxes pra popular o seletor. O sync roda em background (job), então damos um
// tempinho antes de recarregar.
const syncMetaTemplates = async () => {
  const nurtureInboxId = aiNurture.value?.whatsapp_inbox_id;
  if (!nurtureInboxId) return;
  isSyncingTemplates.value = true;
  try {
    await InboxesAPI.syncTemplates(nurtureInboxId);
    await new Promise(resolve => {
      setTimeout(resolve, 3000);
    });
    await store.dispatch('inboxes/get');
    useAlert(t('FORMS_MGMT.EDITOR.DESTINATION.AI_NURTURE.TEMPLATES_SYNCED'));
  } catch {
    useAlert(
      t('FORMS_MGMT.EDITOR.DESTINATION.AI_NURTURE.TEMPLATES_SYNC_ERROR')
    );
  } finally {
    isSyncingTemplates.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-col gap-1">
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ t('FORMS_MGMT.EDITOR.DESTINATION.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11">
        {{ t('FORMS_MGMT.EDITOR.DESTINATION.DESCRIPTION') }}
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <label class="flex flex-col gap-1">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('FORMS_MGMT.EDITOR.DESTINATION.FUNNEL') }}
        </span>
        <select :value="funnelId" class="form-select" @change="onSelectFunnel">
          <option value="">
            {{ t('FORMS_MGMT.EDITOR.DESTINATION.FUNNEL_NONE') }}
          </option>
          <option v-for="funnel in funnels" :key="funnel.id" :value="funnel.id">
            {{ funnel.name }}
          </option>
        </select>
      </label>

      <label class="flex flex-col gap-1">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('FORMS_MGMT.EDITOR.DESTINATION.STAGE') }}
        </span>
        <select v-model="stageName" class="form-select" :disabled="!funnelId">
          <option value="">
            {{ t('FORMS_MGMT.EDITOR.DESTINATION.STAGE_NONE') }}
          </option>
          <option v-for="stage in stages" :key="stage.name" :value="stage.name">
            {{ stage.label || stage.name }}
          </option>
        </select>
      </label>
    </div>

    <FormScoringEditor
      v-model:scoring="scoring"
      :stages="stages"
      :max-points="maxPoints"
    />

    <label class="flex flex-col gap-1">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('FORMS_MGMT.EDITOR.DESTINATION.LEADS_INBOX') }}
      </span>
      <select
        :value="inboxId"
        class="form-select"
        @change="inboxId = $event.target.value || null"
      >
        <option value="">
          {{ t('FORMS_MGMT.EDITOR.DESTINATION.LEADS_INBOX_DEFAULT') }}
        </option>
        <option v-for="ib in allInboxes" :key="ib.id" :value="ib.id">
          {{ ib.name }}
        </option>
      </select>
      <span class="text-xs text-n-slate-11">
        {{ t('FORMS_MGMT.EDITOR.DESTINATION.LEADS_INBOX_HINT') }}
      </span>
    </label>

    <!-- F3.1 — nutrição por IA ao receber o lead -->
    <div class="flex flex-col gap-4 pt-4 mt-2 border-t border-n-weak">
      <label class="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          class="mt-0.5"
          :checked="aiNurture.enabled"
          @change="updateNurture('enabled', $event.target.checked)"
        />
        <span class="flex flex-col gap-0.5">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('FORMS_MGMT.EDITOR.DESTINATION.AI_NURTURE.LABEL') }}
          </span>
          <span class="text-sm text-n-slate-11">
            {{ t('FORM_AI_OPENING.DESCRIPTION') }}
          </span>
        </span>
      </label>

      <div v-if="aiNurture.enabled" class="flex flex-col gap-4 pl-7">
        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('FORMS_MGMT.EDITOR.DESTINATION.AI_NURTURE.INBOX') }}
          </span>
          <select
            :value="aiNurture.whatsapp_inbox_id"
            class="form-select"
            @change="updateNurture('whatsapp_inbox_id', $event.target.value)"
          >
            <option value="">
              {{ t('FORMS_MGMT.EDITOR.DESTINATION.AI_NURTURE.INBOX_NONE') }}
            </option>
            <option
              v-for="inbox in whatsappInboxes"
              :key="inbox.id"
              :value="inbox.id"
            >
              {{ inbox.name }}
            </option>
          </select>
          <span v-if="!whatsappInboxes.length" class="text-xs text-n-amber-11">
            {{ t('FORMS_MGMT.EDITOR.DESTINATION.AI_NURTURE.NO_INBOX') }}
          </span>
        </label>

        <label v-if="aiAgents.length" class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('FORMS_MGMT.EDITOR.DESTINATION.AI_NURTURE.AGENT') }}
          </span>
          <select
            :value="aiNurture.agent_id || ''"
            class="form-select"
            @change="updateNurture('agent_id', $event.target.value || null)"
          >
            <option value="">
              {{ t('FORMS_MGMT.EDITOR.DESTINATION.AI_NURTURE.AGENT_DEFAULT') }}
            </option>
            <option v-for="agent in aiAgents" :key="agent.id" :value="agent.id">
              {{ agent.name }}
            </option>
          </select>
          <span class="text-xs text-n-slate-11">
            {{ t('FORMS_MGMT.EDITOR.DESTINATION.AI_NURTURE.AGENT_HINT') }}
          </span>
        </label>

        <!-- Número oficial (Meta Cloud) exige template pra 1ª mensagem -->
        <div
          v-if="officialNeedsTemplate"
          class="p-3 text-sm rounded-lg text-n-amber-11 bg-n-amber-3"
        >
          {{
            inboxTemplates.length
              ? t(
                  'FORMS_MGMT.EDITOR.DESTINATION.AI_NURTURE.OFFICIAL_PICK_TEMPLATE'
                )
              : t(
                  'FORMS_MGMT.EDITOR.DESTINATION.AI_NURTURE.OFFICIAL_NO_TEMPLATE'
                )
          }}
        </div>

        <button
          v-if="isOfficialInbox"
          type="button"
          class="self-start flex items-center gap-2 px-3 h-9 text-sm font-medium border rounded-lg text-n-slate-12 border-n-weak bg-n-solid-2 hover:bg-n-alpha-2 disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="isSyncingTemplates"
          @click="syncMetaTemplates"
        >
          {{
            isSyncingTemplates
              ? t('FORMS_MGMT.EDITOR.DESTINATION.AI_NURTURE.TEMPLATES_SYNCING')
              : t('FORMS_MGMT.EDITOR.DESTINATION.AI_NURTURE.SYNC_TEMPLATES')
          }}
        </button>

        <label
          v-if="isOfficialInbox && inboxTemplates.length"
          class="flex flex-col gap-1"
        >
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('FORMS_MGMT.EDITOR.DESTINATION.AI_NURTURE.TEMPLATE') }}
          </span>
          <select
            :value="aiNurture.template_id"
            class="form-select"
            @change="onSelectTemplate($event.target.value)"
          >
            <option value="">
              {{ t('FORMS_MGMT.EDITOR.DESTINATION.AI_NURTURE.TEMPLATE_NONE') }}
            </option>
            <option
              v-for="tpl in inboxTemplates"
              :key="tpl.id || tpl.name"
              :value="tpl.id || tpl.name"
            >
              {{ tpl.name }}
            </option>
          </select>
        </label>

        <!-- Preview do template + preenchimento das variáveis (mesmo sistema
             do canvas de campanhas): {{n}} → valor com tokens por lead -->
        <div
          v-if="isOfficialInbox && selectedTemplate && templateBodyText"
          class="flex flex-col gap-2"
        >
          <p
            class="p-3 rounded-lg bg-n-alpha-2 text-sm text-n-slate-11 whitespace-pre-wrap"
          >
            <!-- eslint-disable prettier/prettier -->
            <template v-for="(part, pIdx) in previewParts" :key="pIdx">
<span
                v-if="part.isVar"
                class="text-n-blue-11 font-medium bg-n-blue-9/10 rounded px-0.5"
              >{{ part.text }}</span><template v-else>{{ part.text }}</template>
</template>
            <!-- eslint-enable prettier/prettier -->
          </p>

          <template v-if="templateVariableKeys.length">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('FORMS_MGMT.EDITOR.DESTINATION.AI_NURTURE.VARIABLES') }}
            </span>
            <div
              v-for="key in templateVariableKeys"
              :key="key"
              class="flex items-center gap-1.5"
            >
              <span
                class="text-[11px] font-mono px-1.5 py-1 rounded-md bg-n-alpha-2 text-n-slate-11 shrink-0"
              >
                {{ varLabel(key) }}
              </span>
              <span
                class="i-lucide-arrow-right w-3 h-3 text-n-slate-8 shrink-0"
              />
              <textarea
                :value="templateVariables[key]"
                :rows="
                  Math.min(
                    4,
                    Math.max(
                      1,
                      Math.ceil((templateVariables[key] || '').length / 34)
                    )
                  )
                "
                class="flex-1 min-w-0 !h-auto !min-h-0 !mb-0 px-2 !py-1.5 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 resize-y leading-snug"
                @input="updateTemplateVariable(key, $event.target.value)"
              />
            </div>
            <div class="flex gap-1">
              <button
                v-for="token in ['{{nome}}', '{{empresa}}']"
                :key="token"
                type="button"
                class="px-1.5 py-1 text-[10px] font-mono rounded-md bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3"
                @click="
                  updateTemplateVariable(
                    templateVariableKeys[0],
                    (templateVariables[templateVariableKeys[0]] || '') + token
                  )
                "
              >
                {{ token }}
              </button>
            </div>
            <span class="text-xs text-n-slate-11">
              {{ t('FORMS_MGMT.EDITOR.DESTINATION.AI_NURTURE.VARIABLES_HINT') }}
            </span>
          </template>
        </div>

        <label v-if="!isOfficialInbox" class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('FORM_AI_OPENING.INSTRUCTIONS') }}
          </span>
          <textarea
            :value="aiNurture.opening_instructions"
            rows="4"
            class="form-input"
            :placeholder="t('FORM_AI_OPENING.PLACEHOLDER')"
            @input="updateNurture('opening_instructions', $event.target.value)"
          />
          <span class="text-xs text-n-slate-11">
            {{ t('FORM_AI_OPENING.HINT') }}
          </span>
        </label>

        <!-- Texto livre SÓ para número não-oficial. Oficial (Meta Cloud) usa
             template obrigatoriamente. -->
        <label v-if="!isOfficialInbox" class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('FORM_AI_OPENING.FALLBACK') }}
          </span>
          <textarea
            :value="aiNurture.message_content"
            rows="3"
            class="form-input"
            :placeholder="
              t('FORMS_MGMT.EDITOR.DESTINATION.AI_NURTURE.MESSAGE_PLACEHOLDER')
            "
            @input="updateNurture('message_content', $event.target.value)"
          />
          <span class="text-xs text-n-slate-11">
            {{ t('FORM_AI_OPENING.FALLBACK_HINT') }}
          </span>
        </label>
      </div>
    </div>
  </div>
</template>
